/**
 * Web Push (Erinnerung 24h vor Tippschluss, Contract-Ergänzung Migr. 016).
 * Abo-Verwaltung: Service Worker registrieren, Browser-Berechtigung einholen,
 * PushSubscription anlegen/lösen und beim eigenen Backend hinterlegen.
 */
import { ApiError } from '@/api/client';
import { pushApi } from '@/api/endpoints';

const SW_URL = '/sw.js';

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  return existing ?? navigator.serviceWorker.register(SW_URL);
}

/** Aktuelles Abo, falls vorhanden — für die Anzeige des Aktivieren/Deaktivieren-Status. */
export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64Safe);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}

/** Fordert Berechtigung an, abonniert Push und meldet das Abo beim Backend. */
export async function subscribeToPush(): Promise<void> {
  if (!isPushSupported()) {
    throw new Error('Push-Benachrichtigungen werden von diesem Browser nicht unterstützt.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Berechtigung für Benachrichtigungen wurde nicht erteilt.');
  }

  const registration = await getRegistration();
  let publicKey: string;
  try {
    publicKey = await pushApi.vapidPublicKey();
  } catch (err) {
    if (err instanceof ApiError && err.code === 'push_not_configured') {
      throw new Error('Push ist auf dem Server noch nicht eingerichtet.');
    }
    throw err;
  }
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) {
    throw new Error('Push-Abo konnte nicht erstellt werden.');
  }

  await pushApi.subscribe({ endpoint: json.endpoint, keys: { p256dh, auth } });
}

/** Kündigt das Abo beim Browser und meldet die Abmeldung beim Backend. */
export async function unsubscribeFromPush(): Promise<void> {
  const subscription = await getExistingSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await pushApi.unsubscribe(endpoint);
}
